if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.trackSlideViews();
        this.pinSaleBadge();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Below 750px the gallery is a carousel, and the sale badge (a child of
      // this element, outside the scroller) belongs on the first slide only: it
      // moves with the carousel's scroll, so it slides off with that slide.
      // Measuring scrollLeft rather than a slide keeps it right in both gallery
      // test arms, whichever slide comes first.
      pinSaleBadge() {
        const badge = this.querySelector(':scope > .sale-badge-circle');
        const list = this.elements.viewer && this.elements.viewer.querySelector('ul');
        if (!badge || !list) return;
        const phone = window.matchMedia('(max-width: 749px)');
        const update = () => {
          const x = phone.matches ? list.scrollLeft : 0;
          badge.style.transform = x ? `translateX(${-x}px)` : '';
        };
        list.addEventListener('scroll', update, { passive: true });
        if (phone.addEventListener) phone.addEventListener('change', update);
        update();
      }

      // Gallery A/B test (TASKS.md #149): which slides shoppers actually see, in
      // each arm. Publishes the Shopify customer event jiyu_gallery_slide_viewed
      // (product_handle, arm, position, media_id, image), once per slide per
      // page, for a custom pixel to forward to GA4. Only galleries carrying a
      // challenger report, and only once Intelligems has run, so a held gallery
      // and a QA override send nothing.
      trackSlideViews() {
        const list = this.elements.viewer && this.elements.viewer.querySelector('ul');
        if (!list || !this.hasAttribute('data-gallery-test') || !('IntersectionObserver' in window)) return;

        const seen = new Set();
        const productUrl = (this.closest('product-info') && this.closest('product-info').dataset.url) || '';
        const report = (slide) => {
          const test = window.JiyuGalleryTest;
          const arm = test && test.arm ? test.arm() : null;
          if (!arm || this.hasAttribute('data-gallery-test-pending')) return;
          // The other arm's slides are display: none.
          if (!slide.getClientRects().length || seen.has(slide.dataset.mediaId)) return;
          seen.add(slide.dataset.mediaId);
          const shown = Array.from(list.children).filter((el) => el.getClientRects().length > 0);
          const img = slide.querySelector('.product__media img');
          const src = img ? img.currentSrc || img.src || '' : '';
          try {
            window.Shopify.analytics.publish('jiyu_gallery_slide_viewed', {
              product_handle: productUrl.split('/').pop(),
              arm,
              position: shown.indexOf(slide) + 1,
              media_id: slide.dataset.mediaId.split('-').pop(),
              image: src.split('?')[0].split('/').pop(),
            });
          } catch (e) {}
        };

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) report(entry.target);
            });
          },
          { threshold: 0.6 }
        );
        const observeAll = () => {
          if (!this.isConnected) return;
          Array.from(list.children).forEach((slide) => {
            // Re-observing makes the observer report current visibility again.
            observer.unobserve(slide);
            observer.observe(slide);
          });
        };
        observeAll();
        // A slide that came into view while the arm was being decided wasn't
        // reported then; report it now. Slides added by a pack-size switch are
        // observed as they arrive (childList only, so nothing here can re-trigger it).
        document.addEventListener('jiyu:gallery-test:settled', observeAll);
        new MutationObserver((mutations) => {
          mutations.forEach((m) =>
            m.addedNodes.forEach((node) => {
              if (node.nodeType === 1) observer.observe(node);
            })
          );
        }).observe(list, { childList: true });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
